import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { generationApi, scheduledPostApi } from '../services/api';
import { datum, fehlerText } from '../vertrieb/format';

interface Ergebnis {
  id: string;
  type: string;
  content: string;
  createdAt: string;
  scheduledPost?: { id: string; status: string; scheduledDate: string } | null;
}

const morgenNeun = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d.toISOString();
};

/**
 * Gespeicherte Ergebnisse eines Workflows (GeneratedContent mit workflowId).
 * Bisher landeten sie in der Datenbank und waren von nirgends erreichbar.
 * "Einplanen" legt sie in die Freigabe (morgen 09:00, dort aenderbar).
 */
export const WorkflowErgebnisse: React.FC<{ workflowId: string; limit?: number; neuLaden?: number }> = ({ workflowId, limit = 5, neuLaden = 0 }) => {
  const [liste, setListe] = useState<Ergebnis[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [plant, setPlant] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      const antwort = await generationApi.getGeneratedContent(limit, 0, workflowId);
      setListe(Array.isArray(antwort?.content) ? antwort.content : []);
    } catch (e) {
      setFehler(fehlerText(e, 'Ergebnisse konnten nicht geladen werden'));
      setListe([]);
    }
  }, [workflowId, limit]);

  useEffect(() => { laden(); }, [laden, neuLaden]);

  const einplanen = async (e: Ergebnis) => {
    setPlant(e.id);
    try {
      await scheduledPostApi.schedulePost({ generatedContentId: e.id, scheduledDate: morgenNeun() });
      await laden();
    } catch (err) {
      setFehler(fehlerText(err, 'Einplanen fehlgeschlagen'));
    } finally {
      setPlant(null);
    }
  };

  if (liste === null) return <p className="text-sm text-muted">Ergebnisse werden geladen…</p>;
  return (
    <section aria-label="Gespeicherte Ergebnisse" className="workflow-ergebnisse">
      {fehler && <div className="alert alert-error">{fehler}</div>}
      {liste.length === 0 ? (
        <p className="text-sm text-muted">Noch keine gespeicherten Ergebnisse.</p>
      ) : (
        <ul className="kontakt-liste">
          {liste.map((e) => (
            <li key={e.id}>
              <span className="text-xs text-secondary">{datum(e.createdAt)}</span>{' '}
              <span title={e.content}>{e.content.replace(/\s+/g, ' ').slice(0, 120)}{e.content.length > 120 ? '…' : ''}</span>{' '}
              {e.scheduledPost
                ? <Link to={`/scheduled-posts?id=${encodeURIComponent(e.scheduledPost.id)}`}>in der Freigabe</Link>
                : (
                  <button type="button" className="btn btn-sm btn-ghost" disabled={plant !== null} onClick={() => einplanen(e)} title="Für morgen 09:00 in die Freigabe legen">
                    {plant === e.id ? 'Plant…' : 'Einplanen'}
                  </button>
                )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
